import {
  AnswerOptionVariableDefTypeEnum,
  MasterFieldDef,
  MasterFieldTypeEnum,
  ResponseDatetimeSubTypeEnum,
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  TemplateLayoutTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
  TemplatePageSchema,
  TemplateQuestion,
  TemplateQuestionAnswerOption,
  TraceKey,
} from '@ulysses-inc/harami_api_client'
import { useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import {
  countTraceKey,
  MAX_TRACE_KEYS_PER_TEMPLATE,
} from 'src/exShared/util/nodes/templateNodeTraceKey'
import { findSiblingNodeIds } from 'src/features/templateEdit/common/siblingNodes'
import { RootState } from 'src/state/store'
import { isDefined } from 'src/util/isDefined'
import { isNullish } from 'src/util/isNullish'
import { updateTemplateNode } from './actions'

interface State {
  templatesState: {
    templatePages: {
      templatePages: { [key: number]: TemplatePageSchema }
      templateNodes: { [key: number]: TemplateNodeSchema }
    }
  }
}

export const useTemplateNodeNodes = (
  templateNode: TemplateNodeSchema,
): Array<TemplateNodeSchema> =>
  useSelector(
    (state: State) =>
      templateNode.nodes
        .map(
          (id: number) => state.templatesState.templatePages.templateNodes[id],
        )
        .filter(isDefined) ?? [],
    shallowEqual,
  )

export const useTemplatePageNodes = (
  templatePage: TemplatePageSchema,
): Array<TemplateNodeSchema> =>
  useSelector(
    (state: State) =>
      (templatePage.nodes ?? [])
        .map(
          (id: number) => state.templatesState.templatePages.templateNodes[id],
        )
        .filter(isDefined) ?? [],
    shallowEqual,
  )

export const selectIsGridLayout = (state: RootState) => {
  return (
    state.templatesState.editTemplate.layoutType === TemplateLayoutTypeEnum.Grid
  )
}

const selectAllNodes = (state: RootState) =>
  state.templatesState.templatePages.templateNodes

const selectAllPages = (state: RootState) =>
  state.templatesState.templatePages.templatePages

/**
 * 指定したノードの親(セクション・条件分岐・ページ)の直下にあるノード id のリストを返すセレクター
 *
 * 質問はセクションや条件分岐に属さず、ページ直下に置かれることもある
 */
const selectSiblingNodeIds = (nodeId: number) =>
  createSelector(selectAllNodes, selectAllPages, (allNodes, allPages) =>
    // WARN: ノード数が増えると、ループがネストしているため、ここがパフォーマンスのボトルネックになる
    findSiblingNodeIds(
      nodeId,
      Object.values(allNodes),
      Object.values(allPages),
    ),
  )

/**
 * 日時質問
 */
export type DateTimeQuestion = {
  uuid: string
  name: string
  format: ResponseDatetimeSubTypeEnum
}

/**
 * 指定したノードと同じ親(セクション・条件分岐・ページ)の"直下"の子要素である、日時質問の uuid と name のリストを返すセレクター(孫以下の要素は対象外)
 * 仕様: https://www.notion.so/Web-1d0fd2bccd0e4cff8cf4317c6d45828e
 *
 * @param nodeId ノード id
 * @returns 日時質問のリストのセレクター
 */
export const selectDateTimeQuestionMap = (nodeId: number) =>
  createSelector(
    selectSiblingNodeIds(nodeId),
    selectAllNodes,
    (siblingNodeIds, allNodes) => {
      // 仕様より、同じ親に属する質問のみ取得する
      if (isNullish(siblingNodeIds)) return new Map<string, DateTimeQuestion>()

      /** 同じ親に属する兄弟の質問 */
      const siblingQuestions = siblingNodeIds.reduce<TemplateNodeSchema[]>(
        (nodes, nodeId) => {
          const node = allNodes[nodeId]

          if (isNullish(node)) {
            return nodes
          }

          return [...nodes, node]
        },
        [],
      )

      return siblingQuestions.reduce<Map<string, DateTimeQuestion>>(
        (prev, curr) => {
          const question = curr.question

          const response = question?.responseDatetimes?.[0]
          // 型ガード & 質問種別ガード
          if (
            isNullish(question) ||
            isNullish(response) ||
            question.responseType !== ResponseTypeEnum.DATETIME
          )
            return prev

          // 仕様より、subType(フォーマット) が DATE(日付) は対象外にする
          if (response.subType === ResponseDatetimeSubTypeEnum.DATE) return prev

          const uuid = curr.uuid
          // 型ガード
          if (isNullish(uuid) || isNullish(response.subType)) return prev

          prev.set(uuid, {
            uuid,
            name: question.name || '',
            format: response.subType,
          })
          return prev
        },
        new Map<string, DateTimeQuestion>(),
      )
    },
  )

export const selectQuestionNameByUuid = (state: RootState, uuid: string) => {
  if (uuid === '') return ''

  const node = Object.values(
    state.templatesState.templatePages.templateNodes,
  ).find(node => node.uuid === uuid)

  return node?.question?.name || ''
}

/**
 * 指定された型(fieldType)の、製品マスタの項目一覧を取得するセレクタ
 *
 * @param state
 * @param fieldType
 * @returns
 */
export const selectProductMasterFieldDefsByFieldType = (
  state: RootState,
  fieldType: MasterFieldTypeEnum,
): MasterFieldDef[] => {
  return state.productMasterFieldDefsState.productMasterFieldDefs.filter(
    productMasterFieldDef => productMasterFieldDef.fieldType === fieldType,
  )
}

/**
 * 製品マスタ自動入力(answerOption)を設定/解除するときの question パッチ。
 * 自動入力ONでは値がマスタ参照になるため、必須/時刻表示/逸脱/数値ルールを無効化し、
 * テキストは単一行に戻す。
 */
export const buildAnswerOptionQuestionPatch = (
  answerOption?: TemplateQuestionAnswerOption,
): Partial<TemplateQuestion> => ({
  isRequired: 0,
  isTimeDisplayed: 0,
  deviateComment: undefined,
  deviateNotify: undefined,
  responseNumbers: undefined,
  responseTexts: [{ subType: ResponseTextSubTypeEnum.SINGLE_LINE }],
  answerOption,
})

/**
 * 製品マスタから自動入力の設定に紐づく項目名を取得するセレクタ
 *
 * @param answerOption
 * @returns
 */
export const selectSelectedMasterFieldDefItemName =
  (answerOption?: TemplateQuestionAnswerOption) =>
  (state: RootState): string => {
    if (!answerOption) {
      return ''
    }
    switch (answerOption.variableDefType) {
      case AnswerOptionVariableDefTypeEnum.Name:
        return '製品名'
      case AnswerOptionVariableDefTypeEnum.Code:
        return '製品管理番号'
      case AnswerOptionVariableDefTypeEnum.Item: {
        const productMasterFieldDefs =
          state.productMasterFieldDefsState.productMasterFieldDefs
        return (
          productMasterFieldDefs.find(
            productMasterFieldDef =>
              productMasterFieldDef.id ===
              answerOption.itemVariableDef?.masterFieldDefId,
          )?.name ?? ''
        )
      }
      default:
        return answerOption.variableDefType satisfies never
    }
  }

/**
 * 指定されたノードの traceKeyId を更新するためのカスタムフック
 *
 * @returns
 */
export const useChangeTraceKey = () => {
  const dispatch = useDispatch()

  const changeTraceKey = useCallback(
    (node: TemplateNodeSchema, traceKeyId?: number) => {
      dispatch(
        updateTemplateNode(node.id, {
          ...node,
          ...(traceKeyId
            ? {
                traceKey: {
                  traceKeyId: traceKeyId,
                },
              }
            : { traceKey: undefined }),
        }),
      )
    },
    [dispatch],
  )

  return { changeTraceKey } as const
}

export const selectRemainingTraceKeysCount = (state: RootState): number => {
  const templateNodes = state.templatesState.templatePages.templateNodes
  return MAX_TRACE_KEYS_PER_TEMPLATE - countTraceKey(templateNodes)
}

/**
 * 選択可能な検索キーを取得するセレクタ
 * 同じ検索キーを複数の質問に設定可能なため、全キーを返す
 */
export const selectAvailableTraceKeys =
  (traceKeys: TraceKey[]) =>
  (_state: RootState): TraceKey[] => {
    return traceKeys.filter(({ id }) => id != null)
  }

/**
 * ひな形に含まれるラベル検査質問から、ラベルマスタIDのリストを取得する
 */
export const selectTemplateLabelMasterIds = (state: RootState) =>
  Object.values(state.templatesState.templatePages.templateNodes)
    .filter(node => node.type === TemplateNodeTypeEnum.Question)
    .map(
      node => node.question?.responseLabelChecks?.[0]?.labelInspectionId ?? '',
    )
    .filter(id => id !== '')

export default {
  useTemplateNodeNodes,
  useTemplatePageNodes,
}
